import React, { FC, useState, useEffect } from "react";
import { Modal, Typography, Button } from "antd";
import { MatterExternalMatterLink } from "../matter-external-matter-link";
import { ExternalMatter, ExternalMatterStatus } from "../../data-types/";
import { useFindMatterQuery } from "~/hooks/matters";
import { useExternalMattersQueryLazy } from "~/hooks/external-matters";
import { UPDATE_MATTER } from "~/graphql/mutation";
import { useMutation } from "~/hooks";

export type LinkMatterModalProps = {
  open: boolean;
  onClose: () => void;
  matterId: number;
  pmsName: string;
  onLinkAndConfirm: () => void;
  skip?: boolean;
};

export const LinkMatterModal: FC<LinkMatterModalProps> = ({
  open,
  onClose,
  matterId,
  pmsName,
  onLinkAndConfirm,
  skip,
}) => {
  const { data: matterData } = useFindMatterQuery({ id: matterId, skip: !open });

  const [updateMatter, { loading: updateMatterLoading }] = useMutation(UPDATE_MATTER);

  const matter = matterData?.findMatter;
  const clientId = matter?.client?.id;

  const [selectedExternalMatter, setSelectedExternalMatter] = useState<ExternalMatter | null>(null);

  const externalMatterQuery = useExternalMattersQueryLazy();

  useEffect(() => {
    if (clientId && !skip) {
      externalMatterQuery.get({
        clientId,
        statuses: [ExternalMatterStatus.Unassigned],
      });
    }
  }, [clientId]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Please link proposal to ${pmsName} matter`}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={updateMatterLoading}
          onClick={() => {
            onLinkAndConfirm();
            onClose();
          }}
          disabled={!selectedExternalMatter || updateMatterLoading}
        >
          OK
        </Button>,
      ]}
    >
      <Typography.Paragraph>
        To put this proposal Live you will need to link it to a matter in your PMS. This will enable
        you to track the budget vs actual, get alerts, and keep on top of your matter.
      </Typography.Paragraph>
      {!externalMatterQuery.loading && (
        <MatterExternalMatterLink
          isEditMode={true}
          selectedCurrencyCode={matter?.currency?.code}
          client={matter?.client || null}
          showSwitch={false}
          onChange={(emid) => {
            const externalMatter = externalMatterQuery.data?.items.find((emd) => emd?.id === emid);
            if (externalMatter && emid) {
              setSelectedExternalMatter(externalMatter);
              updateMatter({
                variables: {
                  id: matterId,
                  data: {
                    externalMatter: {
                      connect: {
                        id: emid,
                      },
                    },
                  },
                },
              });
            } else {
              setSelectedExternalMatter(null);
              if (matter?.externalMatter?.id) {
                updateMatter({
                  variables: {
                    id: matterId,
                    data: {
                      externalMatter: {
                        disconnect: {
                          id: {
                            equals: matter?.externalMatter?.id,
                          },
                        },
                      },
                    },
                  },
                });
              }
            }
          }}
          disabled={!matter?.client?.id}
          externalMatterData={externalMatterQuery}
          externalMatterId={null}
          pmsName={pmsName}
        />
      )}
    </Modal>
  );
};
